/**
 * Platen size rules (Phase 86): which platen and platen variant each size of
 * a product type prints on, plus the helpers every platen decision shares.
 *
 * A rule maps product type code, then size code, to exactly one
 * `{ platen, platenVariant }` pair drawn from the shared DTG lists in
 * `./rip-routing` (`DTG_PLATEN_CODES`, `PLATEN_VARIANTS`). This module never
 * declares its own copy of either list, and reuses the exported `getOwn`.
 *
 * Map shape: `productTypeCode -> sizeCode -> { platen, platenVariant }`, stored
 * per org in `org_platen_size_rules`. A per-product override
 * (`org_platen_size_overrides`) beats the rule for that one size (D-12);
 * `resolveEffectivePlatenAssignment` is the only function that answers "what
 * does this size use, and which source won".
 *
 * Everything here is PURE (no I/O) and never throws on stored data: a
 * malformed leaf means "this size has no rule", which means today's
 * behaviour. That keeps a bad row from blocking a print or an upload.
 */
import { DTG_PLATEN_CODES, PLATEN_VARIANTS, RIP_CODE_REGEX, getOwn, } from './rip-routing.js';
import { SIZE_ORDER, getSizeOrder, normalizeSize } from './picklist-sort.js';
import { nameListIncludes } from './product-sync.js';
/** Bound the doc so a hostile or corrupted payload cannot store an unbounded
 *  map. Mirrored by the router's zod (plan 86-07); re-enforced here so a
 *  direct DB write that skipped the zod cannot defeat it. */
export const PLATEN_SIZE_MAX_PRODUCT_TYPES = 64;
export const PLATEN_SIZE_MAX_SIZES_PER_TYPE = 64;
// ---------------------------------------------------------------------------
// Validation helpers.
// ---------------------------------------------------------------------------
function isDtgPlatenCode(value) {
    return typeof value === 'string' && DTG_PLATEN_CODES.includes(value);
}
function isPlatenVariant(value) {
    return typeof value === 'string' && PLATEN_VARIANTS.includes(value);
}
/** A value is an assignment only when both fields are on the closed shared
 *  lists (exact string compare, no case folding). Returns a FRESH object. */
function toAssignment(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return null;
    const platen = getOwn(value, 'platen');
    const platenVariant = getOwn(value, 'platenVariant');
    if (!isDtgPlatenCode(platen) || !isPlatenVariant(platenVariant))
        return null;
    return { platen, platenVariant };
}
function normalizeCode(code) {
    if (typeof code !== 'string')
        return null;
    const upper = code.trim().toUpperCase();
    return upper.length > 0 ? upper : null;
}
// ---------------------------------------------------------------------------
// Sanitizer.
// ---------------------------------------------------------------------------
/**
 * Sanitize an untrusted platen size rules doc into a safe SUBSET. Fail-open,
 * like print size rules (`sanitizePrintSizeRules`): keys are trimmed,
 * uppercased and kept only when they pass `RIP_CODE_REGEX`; a leaf is kept
 * only when its `platen` is in `DTG_PLATEN_CODES` and its `platenVariant` is
 * in `PLATEN_VARIANTS` (exact compare). Anything else is dropped silently. A
 * dropped leaf means that size has no rule, which means today's behaviour.
 *
 * Caps at `PLATEN_SIZE_MAX_PRODUCT_TYPES` types and
 * `PLATEN_SIZE_MAX_SIZES_PER_TYPE` sizes per type; the first N valid entries
 * in iteration order win. Never throws. Returns a FRESH object.
 */
export function sanitizePlatenSizeRules(raw) {
    const out = {};
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw))
        return out;
    for (const [rawType, entry] of Object.entries(raw)) {
        if (Object.keys(out).length >= PLATEN_SIZE_MAX_PRODUCT_TYPES)
            break;
        const typeKey = rawType.trim().toUpperCase();
        if (!RIP_CODE_REGEX.test(typeKey))
            continue;
        if (typeof entry !== 'object' || entry === null || Array.isArray(entry))
            continue;
        const sizes = {};
        for (const [rawSize, leaf] of Object.entries(entry)) {
            if (Object.keys(sizes).length >= PLATEN_SIZE_MAX_SIZES_PER_TYPE)
                break;
            const sizeKey = rawSize.trim().toUpperCase();
            if (!RIP_CODE_REGEX.test(sizeKey))
                continue;
            const assignment = toAssignment(leaf);
            if (!assignment)
                continue;
            sizes[sizeKey] = assignment;
        }
        if (Object.keys(sizes).length === 0)
            continue;
        // Two raw keys can fold to one ('tee' and 'TEE'); merge rather than lose sizes.
        out[typeKey] = { ...(out[typeKey] ?? {}), ...sizes };
    }
    return out;
}
// ---------------------------------------------------------------------------
// Resolution.
// ---------------------------------------------------------------------------
/**
 * The rule table for one product type, or null when the type has no rules.
 * THE one answer to "is this product type ruled" (plan, the worklist, the
 * colour grid, the commit and print time all ask it): the code is trimmed
 * and uppercased, the dual `2` prefix is stripped when the raw type has no
 * table (`2TEE` -> `TEE`, as the print size and RIP routing resolvers do),
 * and an EMPTY table counts as no rules. Never throws.
 */
export function platenRuleTableFor(rules, productTypeCode) {
    const type = normalizeCode(productTypeCode);
    if (!rules || !type)
        return null;
    const nonEmpty = (code) => {
        const table = getOwn(rules, code);
        if (typeof table !== 'object' || table === null || Array.isArray(table))
            return null;
        return Object.keys(table).length > 0 ? table : null;
    };
    return nonEmpty(type) ?? (type.startsWith('2') ? nonEmpty(type.slice(1)) : null);
}
/**
 * The product type codes one rules key reaches: a dual type (`2TEE`) prints
 * on its base type's rules unless it has its own table (`platenRuleTableFor`),
 * so a `TEE` key reaches `2TEE` SKUs too (`['TEE', '2TEE']`). A key that is
 * itself dual, or a base key whose dual has its own table, reaches only
 * itself. `type` is a key of `rules` as stored (normalized). Never throws.
 */
export function ruleTypeCodesFor(rules, type) {
    return type.startsWith('2') || platenRuleTableFor(rules, `2${type}`) !== platenRuleTableFor(rules, type)
        ? [type]
        : [type, `2${type}`];
}
/**
 * The rules key a product type code reads its table under: the code itself,
 * or for a dual code (`2TEE`) with no table of its own, the base key (`TEE`),
 * the same fallback `platenRuleTableFor` reads through. The inverse of
 * `ruleTypeCodesFor`. Never throws.
 */
export function ruledTableKeyFor(rules, code) {
    if (!code.startsWith('2'))
        return code;
    const own = platenRuleTableFor(rules, code);
    return own !== null && own === platenRuleTableFor(rules, code.slice(1)) ? code.slice(1) : code;
}
/**
 * `rules[productTypeCode][sizeCode]` through `platenRuleTableFor`, the size
 * code uppercased. The leaf is re-validated against the shared lists.
 * Returns null for any miss. Never throws.
 */
export function resolvePlatenSizeAssignment(rules, productTypeCode, sizeCode) {
    const size = normalizeCode(sizeCode);
    if (!size)
        return null;
    const table = platenRuleTableFor(rules, productTypeCode);
    return table ? toAssignment(getOwn(table, size)) : null;
}
/**
 * The effective platen assignment for one size of one product: the product's
 * override first, then the org's rule, then nothing (D-12). This is the ONLY
 * function that answers it, and it reports which source won.
 *
 * An override is honoured ONLY while the product type has a rule table
 * (`platenRuleTableFor`): "ruled" means the type has rules on every surface,
 * so an override orphaned by removing its type's rules can never publish or
 * refuse a size that the grid, the worklist and the plan treat as unruled.
 *
 * `overrides` is for ONE product, keyed by uppercase size code; an override
 * value that is not on the shared lists is ignored (the rule then applies).
 * Returns null when neither source has a valid assignment. Never throws.
 */
export function resolveEffectivePlatenAssignment(input) {
    const size = normalizeCode(input.sizeCode);
    if (!size)
        return null;
    const table = platenRuleTableFor(input.rules, input.productTypeCode);
    if (!table)
        return null;
    const override = toAssignment(getOwn(input.overrides ?? undefined, size));
    if (override)
        return { assignment: override, source: 'override' };
    const rule = toAssignment(getOwn(table, size));
    if (rule)
        return { assignment: rule, source: 'rule' };
    return null;
}
// ---------------------------------------------------------------------------
// Platen variant key (one encoding for set mode and the router).
// ---------------------------------------------------------------------------
function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
const PLATEN_ALTERNATION = DTG_PLATEN_CODES.map(escapeRegex).join('|');
const VARIANT_ALTERNATION = PLATEN_VARIANTS.map(escapeRegex).join('|');
/** `<platen>:<variant>`, anchored, built from the shared lists. */
export const PLATEN_VARIANT_KEY_REGEX = new RegExp(`^(${PLATEN_ALTERNATION}):(${VARIANT_ALTERNATION})$`);
/** `{ platen: '14x16', platenVariant: 'Large' }` -> `'14x16:Large'`. */
export function platenVariantKey(assignment) {
    return `${assignment.platen}:${assignment.platenVariant}`;
}
/** Inverse of `platenVariantKey`; null for anything off the shared lists. */
export function parsePlatenVariantKey(key) {
    if (typeof key !== 'string')
        return null;
    const match = PLATEN_VARIANT_KEY_REGEX.exec(key);
    if (!match)
        return null;
    return { platen: match[1], platenVariant: match[2] };
}
// ---------------------------------------------------------------------------
// Served filename token.
// ---------------------------------------------------------------------------
/** A trailing `_<platen>_<variant>` token on a stem. */
const TRAILING_TOKEN_REGEX = new RegExp(`_(${PLATEN_ALTERNATION})_(${VARIANT_ALTERNATION})$`);
/**
 * The served filename of a platen variant file. This is the ONE naming scheme
 * for both upload modes (86-01 item 4).
 *
 * `baseName` is whatever that side would be served under without platen
 * variants. The name is split on its LAST dot (no dot means no extension),
 * any existing trailing `_<platen>_<variant>` token is stripped, and
 * `_<platen>_<variant>` is appended to the stem, so applying it twice
 * replaces rather than stacks.
 *
 * The token uses UNDERSCORES on purpose: `parseDerivedFilename` reads the last
 * two DASH segments as print id and colour, so a dash token would shift the
 * parsed design id and trip the metadata-mismatch early raise in self-heal.
 * The result still satisfies the `print_file_lookups` filename CHECK
 * `^[A-Za-z0-9._-]+$`.
 *
 * Throws only when `assignment` is off the shared lists, which a typed caller
 * cannot produce; the token must never carry an unvetted value into a name.
 */
export function platenVariantPrintFileName(baseName, assignment) {
    const clean = toAssignment(assignment);
    if (!clean) {
        throw new TypeError('platenVariantPrintFileName: platen or platen variant is not on the shared lists');
    }
    const { stem, extension } = splitExtension(baseName);
    const bareStem = stem.replace(TRAILING_TOKEN_REGEX, '');
    return `${bareStem}_${clean.platen}_${clean.platenVariant}${extension}`;
}
/**
 * Product sync's own print file name: `{STEM}-{light|dark|medium}-{front|back}`,
 * an optional platen token (`_14x16_Small`), then the extension
 * (`buildDesignMasterFilename`, `platenVariantPrintFileName`). It carries no
 * composed `{DESIGN}{L|D}-{SHAPE}-{COLOUR}` design token.
 */
const PRODUCT_SYNC_PRINT_FILENAME = /-(?:light|dark|medium)-(?:front|back)(?:_[^_./]+_[^_./]+)?\.[a-z0-9]+$/;
/** True for a file name product sync wrote (see `PRODUCT_SYNC_PRINT_FILENAME`). Pure. */
export function isProductSyncPrintFilename(filename) {
    return PRODUCT_SYNC_PRINT_FILENAME.test(filename);
}
const SERVED_NAME_TYPE_CODE_REGEX = /^[A-Z0-9]{1,8}$/;
/**
 * BLU-1543: the served filename of a platen variant file, with the shape code
 * retired. A platen variant file is chosen by its platen and variant, not by
 * the per-size shape code, so the shape segment of a composed
 * `{DESIGN}-{SHAPE}-{TONE}.arx4` name is replaced by the product type code and
 * every size on one platen variant shares one name (`TH588-R4-CO.arx4` and
 * `TH588-R5-CO.arx4` on 14x16 Small both serve `TH588-TEE-CO_14x16_Small.arx4`).
 * The design id stays the first segment and the tone the last.
 *
 * Any existing token is stripped first. The segment is replaced ONLY when the
 * name ends in `.arx4`, is not a product sync name, its stem has at least
 * three non-empty dash segments, the second-to-last one equals
 * `ctx.shapeCode` (case-insensitive) and the type code is 1 to 8 letters or
 * digits. The shape match is the safety gate: a name that does not carry this
 * SKU's shape code (another scheme, or one already retired) keeps its stem.
 * Then the token is appended (`platenVariantPrintFileName`). Idempotent.
 */
export function platenServedFileName(baseName, assignment, ctx) {
    return platenVariantPrintFileName(retireShapeSegment(stripPlatenVariantToken(baseName), ctx), assignment);
}
function retireShapeSegment(name, ctx) {
    const { stem, extension } = splitExtension(name);
    if (extension !== '.arx4' || isProductSyncPrintFilename(name))
        return name;
    const typeCode = ctx.typeCode.trim().toUpperCase();
    if (!SERVED_NAME_TYPE_CODE_REGEX.test(typeCode))
        return name;
    const segments = stem.split('-');
    if (segments.length < 3 || segments.some((segment) => segment.length === 0))
        return name;
    const shapeAt = segments.length - 2;
    if (segments[shapeAt].toUpperCase() !== ctx.shapeCode.trim().toUpperCase())
        return name;
    segments[shapeAt] = typeCode;
    return `${segments.join('-')}${extension}`;
}
/**
 * The platen assignment a served or catalogue filename carries in its
 * `_<platen>_<variant>` stem token (the inverse of `platenVariantPrintFileName`),
 * or null. Only values on the shared lists match.
 */
export function platenAssignmentFromFileName(name) {
    if (typeof name !== 'string' || name.length === 0)
        return null;
    const match = TRAILING_TOKEN_REGEX.exec(splitExtension(name).stem);
    if (!match)
        return null;
    return { platen: match[1], platenVariant: match[2] };
}
/**
 * `name` without its trailing `_<platen>_<variant>` stem token (the extension
 * is kept). A name with no token is returned unchanged. Used when a side
 * stops being a platen variant file and must be served under its plain name
 * again (the retire path).
 */
export function stripPlatenVariantToken(name) {
    const { stem, extension } = splitExtension(name);
    return `${stem.replace(TRAILING_TOKEN_REGEX, '')}${extension}`;
}
const PLAIN_NAME_SHAPE_CODE_REGEX = /^[A-Za-z0-9]{1,32}$/;
/**
 * BLU-1543: the plain name of a side that stops being a platen variant file,
 * the exact inverse of `platenServedFileName`: the token is stripped and,
 * under the same gate (`.arx4`, not a product sync name, at least three
 * non-empty dash segments), a second-to-last segment equal to `ctx.typeCode`
 * (case-insensitive) gets the SKU's shape code back. The result is the name
 * the side was composed under (`TH588-TEE-CO_14x16_Small.arx4` on
 * `TEE-L-WH-TH588-R5` becomes `TH588-R5-CO.arx4`); any other name only loses
 * its token (`stripPlatenVariantToken`). Idempotent.
 */
export function platenPlainFileName(name, ctx) {
    const plain = stripPlatenVariantToken(name);
    const { stem, extension } = splitExtension(plain);
    if (extension !== '.arx4' || isProductSyncPrintFilename(plain))
        return plain;
    const shapeCode = ctx.shapeCode.trim();
    if (!PLAIN_NAME_SHAPE_CODE_REGEX.test(shapeCode))
        return plain;
    const segments = stem.split('-');
    if (segments.length < 3 || segments.some((segment) => segment.length === 0))
        return plain;
    const typeAt = segments.length - 2;
    if (segments[typeAt].toUpperCase() !== ctx.typeCode.trim().toUpperCase())
        return plain;
    segments[typeAt] = shapeCode;
    return `${segments.join('-')}${extension}`;
}
/** Split on the LAST dot; a leading dot or no dot means no extension. */
function splitExtension(name) {
    const dot = name.lastIndexOf('.');
    return dot > 0 ? { stem: name.slice(0, dot), extension: name.slice(dot) } : { stem: name, extension: '' };
}
// ---------------------------------------------------------------------------
// Size order and runs.
// ---------------------------------------------------------------------------
/**
 * The SKU grammar's two-character plus sizes the shared alias table does not
 * carry (it stops at `3X`). Kept HERE, not in `normalizeSize`: that table also
 * drives the picklist sort and the stocktake CSV, whose order with no platen
 * rules must not change.
 */
const PLATEN_LADDER_ALIASES = {
    '4X': '4XL',
    '5X': '5XL',
};
/**
 * Map a SKU size code onto the shared size ladder: the shared alias table
 * (`normalizeSize`: `XXL`, `2X`, `3X` fold onto `2XL`, `3XL`) plus the platen
 * only `4X`/`5X` fold. Also the 3PL-blank size match's alias pass (BLU-1522),
 * which must agree with the platen ladder; the picklist and stocktake keep
 * `normalizeSize` alone.
 */
export function sizeLadderForm(size) {
    const normalized = normalizeSize(size);
    return PLATEN_LADDER_ALIASES[normalized] ?? normalized;
}
const ladderForm = sizeLadderForm;
/** Rank on the shared `getSizeOrder` ladder, SKU size codes folded first. */
function sizeRank(size) {
    return getSizeOrder(ladderForm(size));
}
/** The default universe: the shared ladder with its alias entry removed. */
const DEFAULT_LADDER = SIZE_ORDER.filter((s) => s !== 'XXL');
/** Ladder families a run never spans (letter sizes, numeric sizes, one size). */
function ladderFamily(size) {
    const index = SIZE_ORDER.indexOf(size);
    if (index < 0)
        return -1;
    if (index <= SIZE_ORDER.indexOf('5XL'))
        return 0;
    if (index <= SIZE_ORDER.indexOf('20'))
        return 1;
    return 2;
}
/**
 * Group sizes into runs that are contiguous in `orderedUniverse`, each run in
 * universe order. Sizes missing from the universe go last, one run each, in
 * input order. Duplicates (case-insensitive) collapse to the first seen.
 *
 * When `orderedUniverse` is omitted the shared `getSizeOrder` ladder is the
 * universe (SKU plus codes like `2X` fold onto `2XL`, and a run never spans
 * the letter, numeric and one-size families). Pure, no i18n: the caller
 * formats the runs (`formatPlatenSizeRange` in the frontend).
 */
export function buildSizeRuns(sizes, orderedUniverse) {
    const seen = new Set();
    const unique = [];
    for (const size of sizes) {
        if (typeof size !== 'string')
            continue;
        const key = size.trim().toUpperCase();
        if (!key || seen.has(key))
            continue;
        seen.add(key);
        unique.push(size);
    }
    const positioned = [];
    const missing = [];
    if (orderedUniverse) {
        const indexByKey = new Map();
        orderedUniverse.forEach((entry, index) => {
            const key = entry.trim().toUpperCase();
            if (!indexByKey.has(key))
                indexByKey.set(key, index);
        });
        for (const size of unique) {
            const position = indexByKey.get(size.trim().toUpperCase());
            if (position === undefined)
                missing.push(size);
            else
                positioned.push({ size, position, family: 0 });
        }
    }
    else {
        for (const size of unique) {
            const ladder = ladderForm(size);
            const position = DEFAULT_LADDER.indexOf(ladder);
            if (position < 0)
                missing.push(size);
            else
                positioned.push({ size, position, family: ladderFamily(ladder) });
        }
    }
    positioned.sort((a, b) => a.position - b.position);
    const runs = [];
    let previous = null;
    for (const item of positioned) {
        const contiguous = previous !== null && item.position === previous.position + 1 && item.family === previous.family;
        if (contiguous)
            runs[runs.length - 1].push(item.size);
        else
            runs.push([item.size]);
        previous = item;
    }
    for (const size of missing)
        runs.push([size]);
    return runs;
}
/**
 * Group items (one per size, or per SKU) by the platen variant their size
 * resolves to through `resolveEffectivePlatenAssignment` (override, then rule).
 * Items inside a group, and the groups themselves, are ordered by the smallest
 * shared `getSizeOrder` rank (input order breaks a tie). An item with a null
 * size code or no assignment lands in `uncovered`, in input order (D-15).
 *
 * This is the ONE grouping the worklist, the colour grid and set mode use; do
 * not re-implement it at a call site.
 */
export function groupSizesByPlatenVariant(items, context) {
    const byKey = new Map();
    const uncovered = [];
    items.forEach((item, index) => {
        const sizeCode = item.sizeCode;
        const effective = sizeCode === null
            ? null
            : resolveEffectivePlatenAssignment({
                rules: context.rules,
                overrides: context.overrides,
                productTypeCode: context.productTypeCode,
                sizeCode,
            });
        if (!effective || sizeCode === null) {
            uncovered.push(item);
            return;
        }
        const key = platenVariantKey(effective.assignment);
        const bucket = byKey.get(key) ?? { assignment: effective.assignment, entries: [] };
        bucket.entries.push({ item, rank: sizeRank(sizeCode), index });
        byKey.set(key, bucket);
    });
    const byRank = (a, b) => a.rank - b.rank || a.index - b.index;
    const groups = [...byKey.entries()]
        .map(([key, bucket]) => {
        const entries = [...bucket.entries].sort(byRank);
        return { key, assignment: bucket.assignment, entries, first: entries[0] };
    })
        .sort((a, b) => byRank(a.first, b.first))
        .map(({ key, assignment, entries }) => ({ key, assignment, items: entries.map((e) => e.item) }));
    return { groups, uncovered };
}
/**
 * True ONLY when the side's marker is exact AND its platen and platen variant
 * equal the size's current assignment (D-22). Every coverage decision in the
 * phase calls this (colour grid, the S4 dialog, the worklist and the
 * print-time needs-file check), so they cannot disagree.
 */
export function isPlatenSideCovered(assignment, sideState) {
    if (!sideState || sideState.exact !== true)
        return false;
    return sideState.platen === assignment.platen && sideState.platenVariant === assignment.platenVariant;
}
/**
 * The sides a ruled size owes a platen variant file for, decided the way
 * print time decides which sides a SKU prints (review 3-S3):
 *  - a dual-sided SKU prints both sides;
 *  - otherwise a side prints when it has artwork (a link, or a platen file
 *    already published on it), so a back-only design owes only a back and a
 *    front-only design only a front;
 *  - a single SKU with no evidence either way prints its front (the plain
 *    single-sided print file), so it owes a front.
 * Front first. The backend worklist and the product sync colour grid both
 * call this, so they can never ask for a side the printer never prints. Pure.
 */
export function requiredPlatenSides(evidence) {
    if (evidence.dualSided)
        return ['front', 'back'];
    const front = evidence.hasFrontUrl === true || evidence.hasFrontFile === true;
    const back = evidence.hasBackUrl === true || evidence.hasBackFile === true;
    if (!front && !back)
        return ['front'];
    const sides = [];
    if (front)
        sides.push('front');
    if (back)
        sides.push('back');
    return sides;
}
/** The BYO SKU letter (`DerivedDesignSet`) to its print set (Phase 86 slice B). */
export const BYO_LETTER_DESIGN_SET = Object.freeze({
    L: 'light',
    D: 'dark',
    M: 'medium',
});
/**
 * The one order every print-set reader walks (Phase 86 slice B, 86-28):
 * light, dark, medium, single. `medium` is BYO-only, so for every other org
 * the walk is today's light, dark, single.
 */
export const DESIGN_SET_ORDER = Object.freeze(['light', 'dark', 'medium', 'single']);
/**
 * The print sets this product needs designs for (mirrors DesignsTier's
 * light/dark vs single split). A colour-less product (no colours) uses only the
 * 'single' set — the backend rejects a light/dark scope on it as
 * 'colourless_invalid_set'. An all-light product also uses 'single'; only a
 * product with at least one dark colour splits into light + dark.
 *
 * Phase 86 slice B (R3-8): `byoSets`, a BYO product's per-variant letters
 * (the operator's choice, else the metafield's), makes the sets exactly the
 * distinct sets its variants read, in light, dark, medium order. A product
 * whose variants all read one set still uses that set's name, never single.
 * A colour-less product stays 'single'. Absent: today's answer, unchanged.
 */
export function requiredSets(colours, darkColoursList, byoSets) {
    if (byoSets !== undefined && colours.length > 0) {
        const read = new Set(byoSets.map((letter) => BYO_LETTER_DESIGN_SET[letter]));
        return DESIGN_SET_ORDER.filter((set) => read.has(set));
    }
    const dark = colours.filter((c) => nameListIncludes(darkColoursList, c));
    const light = colours.filter((c) => !nameListIncludes(darkColoursList, c));
    if (dark.length > 0) {
        const sets = [];
        if (light.length > 0)
            sets.push('light');
        sets.push('dark');
        return sets;
    }
    return ['single'];
}
/**
 * The print set one colour of a product uses: `'single'` when the product's
 * `requiredSets` is `['single']`, otherwise `'dark'` or `'light'` by
 * `darkColours` membership. Matches the apply engine's per-variant choice in
 * `apps/backend/src/modules/pod-product-sync/apply/execute.ts`.
 *
 * Phase 86 slice B: `byoSet`, a BYO variant's own letter, gives that letter's
 * set (a colour-less product stays 'single'). Absent: today's answer.
 */
export function resolveDesignSet(colour, colours, darkColoursList, byoSet) {
    if (byoSet !== undefined && colours.length > 0)
        return BYO_LETTER_DESIGN_SET[byoSet];
    const sets = requiredSets(colours, darkColoursList);
    if (sets.length === 1 && sets[0] === 'single')
        return 'single';
    return nameListIncludes(darkColoursList, colour) ? 'dark' : 'light';
}
// ---------------------------------------------------------------------------
// Synced SKU (moved from the colour grid).
// ---------------------------------------------------------------------------
/**
 * The synced, design-bearing SKU for a variant, or null when it isn't a valid
 * artwork target. A variant qualifies only when it derives cleanly AND its live
 * Shopify SKU already equals the derived print SKU — the commit searches Shopify
 * by that SKU, so anything else would never resolve.
 *
 * `includeDualSided` (the worklist) also accepts a native SKU equal to the
 * derived SKU with the leading `2` added or removed, since a design that gained
 * or lost a back is still the same variant.
 */
export function syncedSku(variant, options = {}) {
    const { nativeSku, derived } = variant;
    if (!derived.ok || typeof derived.sku !== 'string' || !derived.sku)
        return null;
    if (!nativeSku)
        return null;
    if (nativeSku === derived.sku)
        return nativeSku;
    if (!options.includeDualSided)
        return null;
    if (nativeSku === `2${derived.sku}`)
        return nativeSku;
    if (derived.sku.startsWith('2') && nativeSku === derived.sku.slice(1))
        return nativeSku;
    return null;
}
